import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Col, Row } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UserMeta } from 'api/user';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { ImageUpload } from 'components/molecules/image-upload';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  useAvatarUploadMutation,
  useUpdateCurrentUserMutation,
  useUpdateUserMetaMutation,
} from 'queries';
import { fullName } from 'utils/full-name';

const modalMaskCSS = {
  zIndex: 1011,
};

const StyledForm = styled(Form)`
  padding: var(--spacing-05) 0;
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: var(--spacing-05);
`;

const ProfileSetupForm = () => {
  const { user } = useUser();
  const { firstName, lastName } = user;
  const [form] = Form.useForm();
  const firstNameLabel = useTranslation('first_name');
  const firstNamePlaceholder = useTranslation('first_name.placeholder');
  const lastNameLabel = useTranslation('last_name');
  const lastNamePlaceholder = useTranslation('last_name.placeholder');
  const inputRef = useRef<any>(null);
  const [photoUrl, setPhotoUrl] = useState(user.photoUrl);
  const buttonText = `${useTranslation('change_picture')}...`;

  const { mutate: upload, isLoading: isUploading } = useAvatarUploadMutation();

  useEffect(() => {
    inputRef.current!.focus({
      cursor: 'all',
    });
  });

  const mutation = useUpdateUserMetaMutation();
  const currentUserMutation = useUpdateCurrentUserMutation();
  const handleSubmit = (values) => {
    currentUserMutation.mutate(
      { ...values, photoUrl },
      {
        onSuccess: () => {
          mutation.mutate({ metaData: { profileSetupDone: true } });
        },
      },
    );
  };

  const requiredMessage = useTranslation('input.required');
  const handleUpload = ({ file }) => {
    upload(file, {
      onSuccess: (response) => {
        setPhotoUrl(response.location);
      },
    });
  };

  return (
    <StyledForm
      initialValues={{ firstName, lastName }}
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
    >
      <Row gutter={16}>
        <Col span={10}>
          <ImageUpload
            onUpload={handleUpload}
            isLoading={isUploading}
            src={photoUrl}
            alt={fullName(user)}
            buttonText={buttonText}
          />
        </Col>
        <Col span={14}>
          <Form.Item
            name="firstName"
            required
            rules={[{ required: true, message: requiredMessage }]}
            label={firstNameLabel}
          >
            <Input
              autoFocus
              ref={inputRef}
              placeholder={firstNamePlaceholder}
            />
          </Form.Item>
          <Form.Item name="lastName" label={lastNameLabel}>
            <Input placeholder={lastNamePlaceholder} />
          </Form.Item>
        </Col>
      </Row>
      <FormFooter>
        <Button
          loading={mutation.isLoading || currentUserMutation.isLoading}
          onClick={() => form.submit()}
          type="primary"
          icon={<ArrowRight16 />}
          iconPlacement="right"
        >
          <FormattedMessage id="profile.setup.submit" />
        </Button>
      </FormFooter>
    </StyledForm>
  );
};

export const ProfileSetupModal = () => {
  const { userMeta = {} as UserMeta } = useUser();
  const { metaData } = userMeta;

  return (
    <Modal
      width={622}
      visible={!metaData?.profileSetupDone}
      closable={false}
      title={<FormattedMessage id="profile.setup.title" />}
      footer={null}
      maskStyle={modalMaskCSS}
      zIndex={1012}
    >
      <ProfileSetupForm />
    </Modal>
  );
};
